import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useQuery } from '@apollo/client/react';

import { FormSection } from 'common/components/FormSection';
import { useApolloError } from 'common/hooks/useApolloError';
import { ExtraUserFieldsDocument } from 'graphql/types';
import { Loader } from 'common/components/Loader';
import { SwitchField } from 'common/forms/Field';
import { TControllerProps } from 'common/types';
import { TCertificateFormValues } from 'certificate/types';

export const ExtraFieldsSection = ({
    control,
}: TControllerProps<TCertificateFormValues>) => {
    const { showApolloError } = useApolloError();
    const [translate] = useTranslation();
    const [hasExpanded, setHasExpanded] = useState(false);

    const {
        data: extraUserFieldsData,
        loading: extraUserFieldsLoading,
        error: extraUserFieldsError,
    } = useQuery(ExtraUserFieldsDocument, {
        skip: !hasExpanded,
    });

    useEffect(() => {
        if (extraUserFieldsError) {
            showApolloError(extraUserFieldsError);
        }
    }, [extraUserFieldsError, showApolloError]);

    const extraUserFields = extraUserFieldsData?.extraUserFields || [];
    const name = 'extraFields';

    const { setValue } = useFormContext();
    const extraFieldValue = useWatch({ control, name });

    return (
        <FormSection
            renderExpanded
            description={
                <Box>
                    {translate(
                        'certificateForm.userExtraCategoriesDescription'
                    )}
                </Box>
            }
            title={translate('certificateForm.userExtraCategoriesTitle')}
            onExpand={useCallback(() => {
                // Set expanded state once
                if (hasExpanded) return;

                setHasExpanded(true);
            }, [hasExpanded])}
        >
            {extraUserFieldsLoading && <Loader />}

            {!extraUserFieldsLoading &&
                extraUserFields?.map((extraUserField) => (
                    <Box key={extraUserField.name}>
                        <SwitchField
                            checked={extraFieldValue?.includes(
                                extraUserField.name
                            )}
                            control={control}
                            label={extraUserField.name}
                            labelSx={{ ml: 0 }}
                            name={name}
                            onChange={(_e, checked) => {
                                let newValues = [
                                    ...(extraFieldValue || []),
                                ].filter(Boolean);

                                if (
                                    checked &&
                                    !newValues.includes(extraUserField.name)
                                ) {
                                    newValues.push(extraUserField.name);
                                } else {
                                    newValues = newValues.filter(
                                        (value) => value !== extraUserField.name
                                    );
                                }

                                setValue(name, newValues, {
                                    shouldDirty: true,
                                });
                            }}
                        />
                    </Box>
                ))}
        </FormSection>
    );
};
